'use client';

// Spec 113 — one-time celebratory onboarding panel shown before the share
// screen on a creator's FIRST successful market creation (FR-016/FR-017).
// Gated by the device-local `useProposalFirstCreationStore` flag in the
// dialog; this component is purely presentational.

import { useTranslations } from 'next-intl';

import { AnimatedButton } from '@/components/ui/animated-button';

interface ProposalFirstCreationPanelProps {
  /** Created market's title, for the celebratory heading. */
  title: string;
  /** "Let's go" → mark seen, then show the share screen. */
  onContinue: () => void;
}

export function ProposalFirstCreationPanel({
  title,
  onContinue,
}: ProposalFirstCreationPanelProps): React.ReactNode {
  const t = useTranslations('launchpad.form.firstCreation');
  // Listed as literals (not a dynamic map) so the i18n unused-key scanner sees
  // each key statically.
  const steps = [t('step1'), t('step2')];

  return (
    <div className="flex flex-col gap-5 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 motion-safe:duration-normal">
      <div className="flex flex-col gap-2">
        <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-feedback-success-accent/10 px-2.5 py-1 text-caption font-medium uppercase tracking-wider text-feedback-success-content">
          <span className="size-1.5 rounded-full bg-feedback-success-accent" aria-hidden="true" />
          {t('title')}
        </span>
        <h2 className="text-balance font-bold text-heading-lg leading-tight text-foreground-primary">
          {t('subtitle', { title })}
        </h2>
      </div>

      <ol className="flex flex-col gap-2">
        {steps.map((copy, index) => (
          <li
            key={copy}
            className="flex items-center gap-3 rounded-panel-row border bg-background-secondary px-4 py-3"
          >
            <span className="flex size-7 shrink-0 items-center justify-center text-body font-semibold tabular-nums text-foreground-tertiary">
              {index + 1}
            </span>
            <span className="text-body text-foreground-secondary">{copy}</span>
          </li>
        ))}
      </ol>

      <AnimatedButton type="button" size="xl" onClick={onContinue} className="w-full">
        {t('cta')}
      </AnimatedButton>
    </div>
  );
}
